/**
 * Billing cycle projection and exception detection (KAN-37).
 *
 * Two derived artefacts live here, and they are derived in different senses:
 *
 *  - A **billing exception** is a persisted finding about one time entry. It
 *    has to persist because it is *resolvable* — someone marks it reviewed or
 *    corrected and it leaves the active queue. A recomputed-on-read list has
 *    nowhere to record that.
 *  - A **billing forecast** is a snapshot of a projection. It persists so the
 *    figure Finance saw at 09:00 can be told apart from the one at 17:00, and
 *    so each line can be drilled to the records it came from.
 *
 * Neither carries `sourceSystem`/`sourceId`: nothing upstream owns them. Their
 * idempotency key is the exception `fingerprint` (see below) rather than a
 * provenance pair.
 */
/**
 * The four checks KAN-53 names.
 *
 * `no_matter_assigned` is the only one that blocks billing. The others are
 * discrepancies for a person to confirm — a rate that differs from the
 * retainer may well be a deliberate courtesy discount, and refusing to bill it
 * would be the system overruling the person who agreed it.
 */
export const BILLING_EXCEPTION_TYPES = [
    'rate_mismatch',
    'missing_description',
    'possible_duplicate',
    'no_matter_assigned',
    /**
     * More hours logged in one day than the firm's limit (KAN-74).
     *
     * Its own type rather than a flavour of `possible_duplicate`, which the
     * ticket is explicit about: a long day is not a duplicate, and filing it as
     * one would tell Finance the wrong thing to fix. It is also the one rule
     * about a *day* rather than about an entry — it is attached to the longest
     * entry of that day, so the queue has something to open.
     */
    'daily_hours_exceeded',
    /**
     * The same person billing at two different rates on the same matter.
     *
     * Distinct from `rate_mismatch`, and the difference is what it needs to be
     * true. A mismatch compares an entry against an *agreed* rate and so says
     * nothing at all on a matter whose rate table Clio never gave us. This rule
     * compares a person's entries against each other, needs no agreement to
     * exist, and is the only signal available on those matters — which is most of
     * them until the rate sync has run everywhere.
     *
     * Flagged against the rate that person used most often on the matter, so the
     * finding names the odd one out rather than the whole set.
     */
    'inconsistent_rate',
    /**
     * A matter carrying so little unbilled work that billing it this cycle costs
     * more in attention than it collects.
     *
     * Not a defect in the data — every other rule here flags something that is
     * *wrong*, and this one flags something that is merely not worth doing yet.
     * It is in the same queue because it is answered in the same sitting, at the
     * same moment, by the same person: the review before a billing run. Usually a
     * newly opened matter, where the remedy is to defer rather than to correct.
     */
    'wip_below_threshold',
];
/**
 * Where a time entry stands relative to a bill.
 *
 * Three states rather than the `is_billed` boolean the sync used to carry
 * alone, because the middle one is the one that matters: work sitting on a
 * *draft* bill is neither unbilled nor billed. It has been gathered up for a
 * bill that has not gone out, so it is still correctable — and it is precisely
 * the population somebody reviewing a billing run is looking at.
 *
 * Derived rather than stored. `billed` is Clio's own flag on the activity;
 * `draft` is an entry attached to a bill whose status is `draft`; `unbilled` is
 * everything else. A stored column would go stale the moment a draft bill was
 * issued, because issuing it changes the *bill*, and nothing re-syncs the
 * entries hanging off it.
 */
export const BILL_STATUSES = ['unbilled', 'draft', 'billed'];
/**
 * Where one entry stands, from the two things that decide it.
 *
 * The rules, in the order they are applied and for the reasons given:
 *
 *  - **On an issued bill is billed**, whether or not Clio's own `billed` flag
 *    has caught up. The flag and the bill's status are separate fields on
 *    separate collections with separate sync cursors, so they disagree for
 *    minutes at a time; trusting the flag alone would let work that has gone
 *    out to a client keep appearing in a queue of things to fix.
 *  - **A voided bill releases the time.** Void is Clio's way of saying the bill
 *    never happened, and the work on it is unbilled again — which is exactly
 *    when somebody wants to see it in the queue, because it is about to be
 *    billed a second time.
 *  - **Draft is everything in between**: attached to a bill, nothing sent.
 *
 * `null` for `invoiceStatus` means no bill, or a bill that has not synced yet.
 * Both read as unbilled, which is the safe direction — an entry that is
 * *actually* on a draft bill shows up in the queue one pull later rather than
 * being hidden from it.
 */
export function billStatusOf(input) {
    if (input.invoiceStatus === 'void')
        return 'unbilled';
    if (input.invoiceStatus === 'draft')
        return input.isBilled ? 'billed' : 'draft';
    if (input.invoiceStatus !== null)
        return 'billed';
    return input.isBilled ? 'billed' : 'unbilled';
}
export const BILL_STATUS_META = {
    unbilled: {
        label: 'Unbilled',
        description: 'Not on any bill yet. The next billing run is where it goes.',
    },
    draft: {
        label: 'On a draft bill',
        description: 'Gathered onto a bill that has not been issued. Still correctable, and the last point at which it is.',
    },
    billed: {
        label: 'Billed',
        description: 'Already on an issued bill. Shown for reference; correcting it means a credit note.',
    },
};
/**
 * The bill statuses exception detection actually scans.
 *
 * Unbilled and draft, and deliberately not `billed`. The queue is a list of
 * things somebody is going to go and fix, and an entry on an issued bill cannot
 * be fixed — it can only be credited, which is a decision far above this
 * feature's pay grade. Including them would bury the fifty actionable findings
 * under ten thousand historical ones on the first pass.
 *
 * The filter still offers `billed`, and the screen says plainly that it returns
 * nothing and why. An absent filter value would just read as a bug.
 */
export const DETECTED_BILL_STATUSES = [
    'unbilled',
    'draft',
];
/**
 * How a matter is billed, from Clio's `billing_method`.
 *
 * KAN-72 requires flat-fee and contingency matters to be excluded from rate
 * comparison, and this is what makes that possible: there is no hourly rate on
 * such a matter for an entry to differ *from*, so every entry on one would
 * otherwise be flagged.
 *
 * `unknown` is its own value and not a default of `hourly`. A matter whose
 * method Clio did not report is a matter we cannot say anything about, and
 * guessing "hourly" would flag a flat-fee file's every entry on the strength of
 * a missing field.
 */
export const MATTER_FEE_TYPES = ['hourly', 'flat', 'contingency', 'unknown'];
export const MATTER_FEE_TYPE_LABELS = {
    hourly: 'Hourly',
    flat: 'Flat fee',
    contingency: 'Contingency',
    unknown: 'Not stated',
};
/** The only fee type an hourly rate can be compared on. */
export function ratesApplyTo(feeType) {
    return feeType === 'hourly';
}
/**
 * Who a matter's agreed rate applies to (KAN-72's precedence order).
 *
 * Clio's matter rate table holds rows against a named user, against a group
 * ("Paralegal", "Legal Assistant"), or against nobody at all — the last being
 * the matter's flat default for anyone without a more specific row.
 *
 * The order here **is** the precedence: most specific first. It is a list
 * rather than three `if`s so that the rule, the screen and any future fourth
 * level read it from one place.
 */
export const MATTER_RATE_SUBJECTS = ['attorney', 'role', 'matter_default'];
export const MATTER_RATE_SUBJECT_LABELS = {
    attorney: 'This timekeeper',
    role: 'Their role',
    matter_default: 'Matter default',
};
/**
 * The rate a matter's table agrees for one timekeeper, or null for none.
 *
 * Shared between the detection pass and the screen that explains a finding, so
 * the rate a reviewer is shown is the rate the rule compared against — not a
 * second implementation of the precedence that agrees until it does not.
 *
 * Returns the *matched row*, not just the number, because "you billed 550 and
 * the agreement says 450" is a different sentence from "…and the matter default
 * says 450", and the second one is the one that gets challenged.
 *
 * `roleLabel` is the timekeeper's Clio group, which this platform does not sync
 * — see `matter_rates`. Passing null simply skips the role level, which is the
 * honest behaviour: a role rate that cannot be matched to anybody must not be
 * applied to everybody.
 */
export function agreedRateFor(rates, attorneyId, roleLabel = null) {
    for (const subject of MATTER_RATE_SUBJECTS) {
        const match = rates.find((rate) => {
            if (rate.subject !== subject)
                return false;
            if (subject === 'attorney')
                return attorneyId !== null && rate.attorneyId === attorneyId;
            if (subject === 'role') {
                return roleLabel !== null && rate.roleLabel?.toLowerCase() === roleLabel.toLowerCase();
            }
            return true;
        });
        if (match)
            return match;
    }
    return null;
}
/**
 * How sure a rule is, where a rule can be unsure at all (KAN-74).
 *
 * Only duplicate detection uses this. Two entries identical *including* the
 * description are a near-certain repeat; two matching on everything but the
 * description are worth a look and no more. Emitting both at one confidence
 * would either drown the queue or hide the certain ones among the maybes.
 *
 * Null on every other rule, and that is not an omission: a rate mismatch is a
 * difference between two numbers, and dressing it in a confidence would invite
 * somebody to discount it.
 */
export const BILLING_EXCEPTION_CONFIDENCES = ['high', 'low'];
export const BILLING_EXCEPTION_CONFIDENCE_LABELS = {
    high: 'Near-certain',
    low: 'Worth a look',
};
export const DEFAULT_BILLING_RULE_SETTINGS = {
    minDescriptionLength: 10,
    placeholderTerms: ['test', 'tbd', 'xx', '.', 'asdf', 'n/a', 'na', 'tba', 'todo'],
    dailyHoursLimit: 16,
    minWipMinor: 10_000,
    updatedAt: null,
};
/**
 * Why a description was flagged, so the queue can say which rule tripped rather
 * than leaving a reviewer to work it out from the text.
 */
export const DESCRIPTION_FLAGS = ['empty', 'too_short', 'placeholder'];
export const DESCRIPTION_FLAG_LABELS = {
    empty: 'No description',
    too_short: 'Too short to bill against',
    placeholder: 'Placeholder text',
};
/**
 * Classifies a description against the firm's settings, or null when it passes.
 *
 * Shared so the queue, the detection pass and any preview agree — and so the
 * order of the checks is stated once, because it changes what a reviewer is
 * told. Empty first, since "too short" on an empty description would read as
 * the rule miscounting. Then **placeholder before too-short**: "tbd" is both,
 * and knowing an entry is junk is more actionable than knowing it is terse.
 * KAN-73's own test table expects exactly that.
 */
export function classifyDescription(description, settings) {
    const trimmed = (description ?? '').trim();
    if (trimmed === '')
        return 'empty';
    const normalised = trimmed.toLowerCase();
    if (settings.placeholderTerms.some((term) => term.toLowerCase() === normalised)) {
        return 'placeholder';
    }
    if (trimmed.length < settings.minDescriptionLength)
        return 'too_short';
    return null;
}
export const BILLING_EXCEPTION_STATUSES = ['open', 'resolved'];
/**
 * How an exception left the queue.
 *
 * `corrected` and `superseded` are set by detection; `accepted` and
 * `not_an_exception` can only come from a person. The distinction matters when
 * reading the queue's history: "the data was fixed" and "a human decided this
 * was fine" are different outcomes, and only one of them is re-checkable.
 */
export const BILLING_EXCEPTION_RESOLUTIONS = [
    'corrected',
    'accepted',
    'not_an_exception',
    'superseded',
];
/** The resolutions a person may choose. The other two are detection's to set. */
export const HUMAN_RESOLUTIONS = [
    'corrected',
    'accepted',
    'not_an_exception',
];
export const BILLING_EXCEPTION_META = {
    no_matter_assigned: {
        label: 'No matter assigned',
        description: 'Billable time with no matter. It cannot be billed to anyone, so it is held out of the forecast until a matter is set.',
        severity: 'blocking',
    },
    rate_mismatch: {
        label: 'Rate differs from retainer',
        description: "The rate on the entry is not the rate agreed on the matter in Clio. The whole rate table is shown beside it — including any rates set against a role, which are listed but cannot be matched to a person here. Both figures travel with the finding so the difference can be confirmed or corrected rather than guessed at.",
        severity: 'review',
    },
    missing_description: {
        label: 'Missing or inadequate description',
        description: 'Billable time with no narrative, one too short to bill against, or placeholder text. An invoice line a client cannot read invites a write-down.',
        severity: 'review',
    },
    possible_duplicate: {
        label: 'Possible duplicate',
        description: 'Same attorney, same day, same matter, same duration as an earlier entry. Near-certain when the descriptions match too; worth a look when they differ. Usually a double-save, occasionally two genuinely identical pieces of work.',
        severity: 'review',
    },
    daily_hours_exceeded: {
        label: 'Implausible hours in one day',
        description: 'More hours logged in a single day than the firm allows for. Its own signal rather than a duplicate: a long day is not a repeated entry, and the fix is different.',
        severity: 'review',
    },
    inconsistent_rate: {
        label: 'Rate inconsistent for this timekeeper',
        description: 'The same person has billed this matter at more than one rate. Flagged against the rate they used most often, so the odd entry out is the one named. Needs no retainer on record, which is why it catches what the retainer comparison cannot.',
        severity: 'review',
    },
    wip_below_threshold: {
        label: 'Too little unbilled work to bill',
        description: 'The matter has less unbilled value than the firm bills for. Usually newly opened, and usually better deferred to the next cycle than billed now. Not a defect — the money stays in the forecast.',
        severity: 'review',
    },
};
export const BILLING_EXCEPTION_RESOLUTION_META = {
    corrected: {
        label: 'Corrected',
        description: 'The underlying entry was fixed. Re-checked on the next detection pass.',
    },
    accepted: {
        label: 'Reviewed and accepted',
        description: 'The entry is right as it stands. Not re-raised.',
    },
    not_an_exception: {
        label: 'Not an exception',
        description: 'The rule does not apply to this entry. Not re-raised.',
    },
    superseded: {
        label: 'Superseded',
        description: 'The entry was billed or removed while the exception was still open.',
    },
};
export const BILLING_FORECAST_TRIGGERS = ['scheduled', 'sync', 'manual'];
/** Trailing window the realization rate is measured over. */
export const REALIZATION_LOOKBACK_MONTHS = 12;
/**
 * Assumed realization when there is no invoice history to measure.
 *
 * 100% rather than a guessed haircut: with no history, any other number is
 * invented, and an invented haircut is harder to notice than a rate of 100%
 * sitting next to a sample size of zero.
 */
export const DEFAULT_REALIZATION_RATE_BPS = 10_000;
//# sourceMappingURL=billing.js.map